import type { ChannelMember } from '@src/api/shared/community/channelMemberApiClient';
import { useChatSend } from '@src/hooks/useChatSend';
import {
  AttachmentPreview,
  EditorFeatures,
  TiptapEditorCore,
  TiptapEditorHandle,
  useTiptapFileHandler,
} from '@src/ui/Editor';
import { CommunityEditorConfig } from '@src/ui/Editor/constants/editorConfig';
import { Editor } from '@tiptap/react';
import { EmojiClickData } from 'emoji-picker-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { CommunityToolbar } from './CommunityToolbar';
import EditorChatFooter from './EditorChatFooter';
import { CommunityTiptapEditorProps } from './types';

export interface CommunityToolbarEventHandlers {
  onSelectFile?: (files: FileList) => void;
  handleOpenLinkAddModal?: () => void;
}

interface EditorViewWithUpdate {
  updateChannelMembers?: (members: ChannelMember[]) => void;
}

/**
 * CommunityTiptapEditor
 * Specialized editor for community context with specific features enabled
 */
export const CommunityTiptapEditor = forwardRef<TiptapEditorHandle, CommunityTiptapEditorProps>(
  (
    {
      value,
      onChange,
      enabled,
      height,
      width,
      containerHeight,
      editorRef,
      isInitFocus = false,
      onSend,
      attachments,
      onAttachmentChange,
      channelCode,
      channelName,
      channelMembers,
      channelVisibility,
      isAnonymous = false,
      onChangeIsAnonymous,
      isEditing = false,
      editedChatId,
      allowFileAttachment,
      allowAnonymousPost,
      onCancelEdit,
      currentUserId,
      currentUserType,
      isLoggedIn = false,
      onSelectEmoji,
      onTriggerMention,
      isMentionListActive,
      onSelectFile,
      enableLinkHoverTooltip = true,
      enableLinkClickModal = true,
      ...rest
    },
    ref,
  ) => {
    const internalEditorRef = useRef<{ getEditor: () => Editor | null }>(null);
    const [chatErrorMessage, setChatErrorMessage] = useState<string | null>(null);
    const [isEmpty, setIsEmpty] = useState<boolean>(true);

    const { uploadAttachment, deleteAttachment, createMessage, updateMessage, isSending } =
      useChatSend(channelCode, setChatErrorMessage);

    const fileHandler = useTiptapFileHandler({
      editor: internalEditorRef.current?.getEditor() || null,
      editorConfig: CommunityEditorConfig,
      useAlerts: false,
      enableImageInsertion: false,
      attachments: attachments || [],
      onAttachmentChange,
      uploadAttachmentApi: uploadAttachment,
      deleteAttachmentApi: deleteAttachment,
      isEditing,
    });

    // Ref to hold eventHandlers from TiptapEditorCore
    const coreEventHandlers = useRef<CommunityToolbarEventHandlers>({});

    // Callback to receive eventHandlers from TiptapEditorCore
    const handleEventHandlersReady = (eventHandlers: CommunityToolbarEventHandlers) => {
      coreEventHandlers.current = eventHandlers;
    };

    const defaultCommunityFeatures: EditorFeatures = useMemo(
      () => ({
        richText: true,
        mentions: true,
        images: true,
        links: true,
        tables: false,
        iframes: false,
        textStyles: true,
        lists: true,
        codeBlocks: true,
        quotes: true,
        dividers: true,
        history: true,
        dragDrop: true,
        fileUpload: allowFileAttachment,
        fileSave: false,
        shortcut: {
          underline: false,
          strike: true,
          heading: false,
          textAlign: false,
          blockquote: true,
        },
      }),
      [allowFileAttachment],
    );

    const finalFeatures = useMemo(
      () => (enabled ? { ...defaultCommunityFeatures, ...enabled } : defaultCommunityFeatures),
      [enabled, defaultCommunityFeatures],
    );

    useEffect(() => {
      const editor = internalEditorRef.current?.getEditor();
      if (!editor) {
        const hasContent = value && value.trim().length > 0;
        const hasAttachments = attachments && attachments.length > 0;
        setIsEmpty(!hasContent && !hasAttachments);
        return;
      }

      const checkIsEmpty = () => {
        const text = editor.getText().trim();
        const hasAttachments = attachments && attachments.length > 0;
        setIsEmpty(text.length === 0 && !hasAttachments);
      };

      checkIsEmpty();

      editor.on('update', checkIsEmpty);

      return () => {
        editor.off('update', checkIsEmpty);
      };
    }, [value, attachments]);

    useEffect(() => {
      const editor = internalEditorRef.current?.getEditor();
      if (!editor || !channelMembers) {
        return;
      }

      const editorView = editor.view as EditorViewWithUpdate;
      if (editorView.updateChannelMembers) {
        editorView.updateChannelMembers(channelMembers);
      }
    }, [channelMembers]);

    const handleEmojiSelect = (emojiObject: EmojiClickData) => {
      const editor = internalEditorRef.current?.getEditor();
      if (editor) {
        editor.chain().focus().insertContent(emojiObject.emoji).run();
      }
      onSelectEmoji?.(emojiObject);
    };

    const handleTriggerMention = () => {
      const editor = internalEditorRef.current?.getEditor();
      if (editor && isLoggedIn && channelMembers && channelMembers.length > 0) {
        editor.chain().focus().insertContent('@').run();
      }
      onTriggerMention?.();
    };

    const handleChatSend = useCallback(async () => {
      const editor = internalEditorRef.current?.getEditor();
      if (!editor) {
        return;
      }

      const html = editor.getHTML();
      const text = editor.getText().trim();
      const hasAttachments = attachments && attachments.length > 0;

      if (text.length === 0 && !hasAttachments) {
        return;
      }

      let success = false;
      if (isEditing && editedChatId != null) {
        success = await updateMessage(html, text, attachments || [], isAnonymous, editedChatId);
        if (!success) {
          return;
        }
      } else {
        success = await createMessage(html, text, attachments || [], isAnonymous);
      }

      if (!success) {
        return;
      }

      onChangeIsAnonymous?.(false);

      if (!isEditing) {
        editor.chain().clearContent().run();

        onChange('');

        onAttachmentChange?.([]);
        fileHandler.resetErrorMessage();

        setIsEmpty(true);
      }

      onSend?.();
    }, [
      attachments,
      isEditing,
      editedChatId,
      updateMessage,
      createMessage,
      onChange,
      onAttachmentChange,
      fileHandler,
      onSend,
    ]);

    useImperativeHandle(ref, () => ({
      getEditor: () => internalEditorRef.current?.getEditor() || null,
      resetErrorMessage: fileHandler.resetErrorMessage,
      selectFiles: fileHandler.handleSelectFile,
      setAttachmentError: fileHandler.setAttachmentError,
      blur: () => {
        const editor = internalEditorRef.current?.getEditor();
        editor?.commands.blur();
      },
    }));

    return (
      <div
        className='flex flex-col rounded border border-[#ccc]'
        data-editor-container
      >
        <TiptapEditorCore
          ref={internalEditorRef}
          features={finalFeatures}
          value={value}
          onChange={onChange}
          height={height}
          width={width}
          containerHeight={containerHeight}
          editorRef={editorRef}
          isInitFocus={isInitFocus}
          enableMentions={true}
          enablePasteFiltering={true}
          placeholderText={channelName ? `${channelName}へのメッセージ` : 'Type a message'}
          className='max-h-[220px] min-h-10 overflow-y-auto px-4 py-[9px] lg:max-h-[160px]'
          onSend={onSend}
          attachments={attachments}
          onAttachmentChange={onAttachmentChange}
          channelCode={channelCode}
          channelName={channelName}
          channelMembers={channelMembers}
          channelVisibility={channelVisibility}
          isEditing={isEditing}
          editedChatId={editedChatId}
          allowFileAttachment={allowFileAttachment}
          onCancelEdit={onCancelEdit}
          currentUserId={currentUserId}
          currentUserType={currentUserType}
          isLoggedIn={isLoggedIn}
          isCommunityEditor={true}
          onSelectEmoji={onSelectEmoji}
          onTriggerMention={onTriggerMention}
          isMentionListActive={isMentionListActive}
          onSelectFile={onSelectFile}
          uploadFunction={undefined}
          onDragEnter={undefined}
          onDragLeave={undefined}
          onDrop={undefined}
          onSendShortcut={handleChatSend}
          disabled={isSending || isEmpty}
          enableLinkHoverTooltip={enableLinkHoverTooltip}
          enableLinkClickModal={enableLinkClickModal}
          onEventHandlersReady={handleEventHandlersReady}
          {...rest}
        >
          <CommunityToolbar
            editor={internalEditorRef.current?.getEditor() || null}
            features={finalFeatures}
            eventHandlers={{
              onSelectFile: fileHandler.handleSelectFile,
              handleOpenLinkAddModal: coreEventHandlers.current.handleOpenLinkAddModal,
            }}
          />
          {/* 編集中のときに表示する */}
          {isEditing && (
            <div className='h-[30px] bg-gray-280 px-4 py-1 text-sm'>メッセージ編集中</div>
          )}
        </TiptapEditorCore>

        {attachments && attachments.length > 0 && allowFileAttachment && (
          <AttachmentPreview
            attachments={attachments}
            onDelete={fileHandler.handleDeleteFile}
            allowFileAttachment={allowFileAttachment}
          />
        )}

        {/* Attachment Error Display */}
        {fileHandler.attachmentError && (
          <div className='tiptap-error-message'>{fileHandler.attachmentError}</div>
        )}

        {/* Chat Error Display */}
        {chatErrorMessage && <div className='tiptap-error-message'>{chatErrorMessage}</div>}

        <EditorChatFooter
          features={finalFeatures}
          onSend={handleChatSend}
          onSelectFile={fileHandler.handleSelectFile}
          channelCode={channelCode}
          channelName={channelName}
          channelMembers={channelMembers}
          channelVisibility={channelVisibility}
          isAnonymous={isAnonymous}
          onChangeIsAnonymous={onChangeIsAnonymous}
          isEditing={isEditing}
          editedChatId={editedChatId}
          allowFileAttachment={allowFileAttachment}
          allowAnonymousPost={allowAnonymousPost}
          onCancelEdit={onCancelEdit}
          currentUserId={currentUserId}
          currentUserType={currentUserType}
          isLoggedIn={isLoggedIn}
          onSelectEmoji={handleEmojiSelect}
          onTriggerMention={handleTriggerMention}
          isMentionListActive={isMentionListActive}
          disabled={isSending || isEmpty}
        />
      </div>
    );
  },
);

CommunityTiptapEditor.displayName = 'CommunityTiptapEditor';
